import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/components/ui/use-toast';
import { CRM_FOCUS_KEY, CRM_OPEN_ACCOUNT_KEY, consumeFocusSearch } from '@/lib/adminNav';
import { supabase } from '@/lib/supabase';
import { formatDate } from '@/lib/dates';
import {
  Search,
  Plus,
  Download,
  Loader2,
  RefreshCw,
  Building2,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  CheckCircle2,
  XCircle,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  Link2,
  ShieldCheck,
  CalendarClock,
} from 'lucide-react';

import CRMAccountForm, { type CRMAccount, type CRMFormFocus } from './CRMAccountForm';
import CRMAccountDetail from './CRMAccountDetail';
import BulkBackfillDialog from './BulkBackfillDialog';
import { ACCOUNT_SERIES_KEYS, ACCOUNT_SERIES_LABELS } from '@/lib/accountSeries';

const PAGE_SIZE = 25;

const CATEGORY_OPTIONS = [
  { value: 'all', label: 'All Categories' },
  { value: 'Parent', label: 'Parent' },
  { value: 'Sub Account', label: 'Sub Account' },
  { value: 'Traveller', label: 'Traveller' },
  { value: 'Corporate', label: 'Corporate' },
  { value: 'Personal', label: 'Personal' },
  { value: 'Facility', label: 'Facility' },
  { value: 'Funder', label: 'Funder' },
  { value: 'Internal', label: 'Internal' },
  { value: 'Other', label: 'Other' },
  { value: '__none', label: 'Uncategorised' },
];

const SERIES_KEYS = ACCOUNT_SERIES_KEYS;
const SERIES_LABELS = ACCOUNT_SERIES_LABELS;

interface JobCountMap {
  [accountId: string]: number;
}

interface ApprovalSummaryMap {
  [accountId: string]: {
    status: string | null;
    daysRemaining: number | null;
    isOpen: boolean;
    approvalTo: string | null;
  };
}

interface PermanentBookingSummaryMap {
  [accountId: string]: {
    activeCount: number;
    nextExpiry: string | null;
    nextDaysRemaining: number | null;
  };
}

type SortField =
  | 'account_name'
  | 'account_id'
  | 'account_category'
  | 'is_active'
  | 'is_fixed_location_number'
  | 'always_divert_to_operator'
  | 'job_count'
  | 'defaults_status'
  | 'approval_status'
  | 'approval_days'
  | 'perm_count'
  | 'perm_days'
  | 'home_address';

// Plain columns on crm_accounts -- sorted server-side across the whole
// filtered set. Everything else (job counts, approval/perm summaries, home
// address) comes from a separate table/view only loaded for the current
// page, so those sort within the page only -- same tradeoff already made
// for approval_days/perm_days.
const SERVER_SORT_FIELDS: SortField[] = [
  'account_id',
  'account_name',
  'account_category',
  'is_active',
  'is_fixed_location_number',
  'always_divert_to_operator',
];

// Everything not in SERVER_SORT_FIELDS -- job counts, defaults completeness,
// approval/perm status & days, home address -- sorts within the current
// page only, since those come from separate tables/maps only loaded for
// whatever's visible.
const CLIENT_SORT_FIELDS: SortField[] = [
  'job_count',
  'defaults_status',
  'approval_status',
  'approval_days',
  'perm_count',
  'perm_days',
  'home_address',
];

type TriFilter = 'all' | 'yes' | 'no';

/** Matches getLifecycle()'s return type -- used to filter the Approval and
 *  Perm Bookings columns across the WHOLE account set (not just whatever's
 *  on the current page), same "load a full id set once" approach already
 *  used for the Home Address filter below. */
type LifecycleFilter = 'all' | 'current' | 'expiring' | 'expired' | 'open' | 'none';

/**
 * Derive a "lifecycle" status from days remaining.
 * Current: > 90 days
 * Expiring: 0 - 90 days
 * Expired: < 0 days
 * Open: open approval (no end date)
 */
const getLifecycle = (
  days: number | null | undefined,
  isOpen?: boolean
): 'open' | 'current' | 'expiring' | 'expired' | 'none' => {
  if (isOpen) return 'open';
  if (days === null || days === undefined) return 'none';
  if (days < 0) return 'expired';
  if (days <= 90) return 'expiring';
  return 'current';
};

const getDaysCellClass = (lifecycle: ReturnType<typeof getLifecycle>) => {
  switch (lifecycle) {
    case 'current':
      return 'text-green-400';
    case 'expiring':
      return 'text-amber-400';
    case 'expired':
      return 'text-red-400 font-medium';
    case 'open':
      return 'text-blue-400';
    default:
      return 'text-muted-foreground';
  }
};

const getLifecycleBadgeClass = (lifecycle: ReturnType<typeof getLifecycle>) => {
  switch (lifecycle) {
    case 'current':
      return 'bg-green-900/40 text-green-400 border-green-700/50';
    case 'expiring':
      return 'bg-amber-900/30 text-amber-400 border-amber-700/50';
    case 'expired':
      return 'bg-red-900/30 text-red-400 border-red-700/50';
    case 'open':
      return 'bg-blue-900/40 text-blue-400 border-blue-700/50';
    default:
      return 'bg-secondary text-muted-foreground border-border';
  }
};

const getLifecycleLabel = (lifecycle: ReturnType<typeof getLifecycle>) => {
  switch (lifecycle) {
    case 'current':
      return 'Current';
    case 'expiring':
      return 'Expiring';
    case 'expired':
      return 'Expired';
    case 'open':
      return 'Open';
    default:
      return 'None';
  }
};

const formatDaysText = (days: number | null | undefined, isOpen?: boolean) => {
  if (isOpen) return 'Open';
  if (days === null || days === undefined) return '-';
  if (days < 0) return `Expired ${Math.abs(days)}d`;
  if (days === 0) return 'Today';
  return `${days}d left`;
};

const CRMAccountsPage: React.FC = () => {
  const [subAccountConfig, setSubAccountConfig] = useState<{
    parentAccountId: string;
    parentAccountName: string;
    mode: 'sub-account' | 'traveller' | 'ooa';
  } | null>(null);

  const [accounts, setAccounts] = useState<CRMAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);

  // Search is split into two pieces of state:
  //   * `searchInput` — bound to the <Input> so typing is instant and never
  //     triggers a network request mid-keystroke (which was causing the
  //     "loses focus after one character" behaviour).
  //   * `search` — the debounced value actually used by loadAccounts().
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  // Debounce the input → search transition so we don't refetch on every
  // keystroke. This keeps the input's focus stable and dramatically reduces
  // DB load while typing.
  useEffect(() => {
    const trimmed = searchInput.trim();
    const handler = setTimeout(() => {
      setSearch(trimmed);
      setPage(0);
    }, 300);
    return () => clearTimeout(handler);
  }, [searchInput]);

  // Cross-tab navigation: pre-fill search from sessionStorage (set by
  // TM Client Coverage when "Open in CRM" is clicked) and stay reactive
  // to subsequent navigations while the CRM tab stays mounted.
  useEffect(() => {
    const focus = consumeFocusSearch(CRM_FOCUS_KEY);
    if (focus) setSearchInput(focus);

    // Opens a client's detail panel straight from an account number (e.g. from
    // the Closed Check approval alerts) -- fetched directly so it doesn't
    // depend on the list's current filters or page.
    const openById = async (accountId: string) => {
      const { data } = await supabase.from('crm_accounts').select('*').eq('account_id', accountId).maybeSingle();
      if (data) {
        setSelectedAccount(data as CRMAccount);
        setDetailOpen(true);
      }
    };
    const pendingOpen = consumeFocusSearch(CRM_OPEN_ACCOUNT_KEY);
    if (pendingOpen) openById(pendingOpen);

    const onNav = (e: Event) => {
      const detail = (e as CustomEvent<{ tab: string; search?: string; openAccount?: string }>).detail;
      if (detail?.tab === 'crm' && detail.search) {
        setSearchInput(detail.search);
      }
      if (detail?.tab === 'crm' && detail.openAccount) {
        consumeFocusSearch(CRM_OPEN_ACCOUNT_KEY);
        openById(detail.openAccount);
      }
    };
    window.addEventListener('app:admin-navigate', onNav);
    return () => window.removeEventListener('app:admin-navigate', onNav);
  }, []);


  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  // Pill-style filters (like export builder)
  const [billedFilter, setBilledFilter] = useState<'all' | 'billed' | 'non-billed'>('all');
  const [seriesFilter, setSeriesFilter] = useState<string[]>(SERIES_KEYS);
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [fixedLocationFilter, setFixedLocationFilter] = useState<TriFilter>('all');
  const [alwaysDivertFilter, setAlwaysDivertFilter] = useState<TriFilter>('all');
  const [homeAddressFilter, setHomeAddressFilter] = useState<TriFilter>('all');
  const [approvalFilter, setApprovalFilter] = useState<LifecycleFilter>('all');
  const [permBookingFilter, setPermBookingFilter] = useState<LifecycleFilter>('all');
  const [sortField, setSortField] = useState<SortField>('account_name');
  const [sortAsc, setSortAsc] = useState(true);

  // account_ids with an active "home" row in crm_account_addresses --
  // loaded once (whole table, not per-page) so the Home Address filter and
  // the Home Address column both work against the full account set, not
  // just whatever's on the current page.
  const [homeAddressAccountIds, setHomeAddressAccountIds] = useState<Set<string>>(new Set());
  const [homeAddressIdsLoaded, setHomeAddressIdsLoaded] = useState(false);
  const [loadingHomeAddressIds, setLoadingHomeAddressIds] = useState(false);

  // account_id -> lifecycle, for the WHOLE account set (not just the
  // current page) -- loaded once so the Approval/Perm Bookings filters
  // below can narrow the full ~4000-account list, same as Home Address.
  const [approvalLifecycleMap, setApprovalLifecycleMap] = useState<Map<string, { lifecycle: LifecycleFilter; days: number | null }>>(
    new Map()
  );
  const [approvalLifecycleLoaded, setApprovalLifecycleLoaded] = useState(false);
  const [permLifecycleMap, setPermLifecycleMap] = useState<Map<string, { lifecycle: LifecycleFilter; days: number | null }>>(
    new Map()
  );
  const [permLifecycleLoaded, setPermLifecycleLoaded] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<CRMAccount | null>(null);
  // Set when the edit form was opened from a client's detail panel (e.g. an
  // overview tile), so closing the form drops you back on that client.
  const [editFocus, setEditFocus] = useState<CRMFormFocus | null>(null);
  const reopenDetailAfterEdit = useRef<string | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<CRMAccount | null>(null);
  const [bulkBackfillOpen, setBulkBackfillOpen] = useState(false);

  const [jobCounts, setJobCounts] = useState<JobCountMap>({});
  const [approvalSummaries, setApprovalSummaries] = useState<ApprovalSummaryMap>({});
  const [permBookingSummaries, setPermBookingSummaries] = useState<PermanentBookingSummaryMap>({});
  const [loadingCounts, setLoadingCounts] = useState(false);
  const [loadingApprovals, setLoadingApprovals] = useState(false);
  const [loadingPermBookings, setLoadingPermBookings] = useState(false);

  const [togglingId, setTogglingId] = useState<string | null>(null);

  // Server-side sort is applied for plain crm_accounts columns; everything
  // else (job counts, approval/perm summaries, home address) sorts within
  // the current page only, in the sortedAccounts memo below.
  const serverSortField: SortField = SERVER_SORT_FIELDS.includes(sortField) ? sortField : 'account_name';

  const loadHomeAddressIds = useCallback(async () => {
    setLoadingHomeAddressIds(true);
    try {
      // Paged: a single Supabase request stops at 1000 rows.
      const ids = new Set<string>();
      const PAGE = 1000;
      for (let from = 0; ; from += PAGE) {
        const { data, error } = await supabase
          .from('crm_account_addresses')
          .select('account_id')
          .eq('address_type', 'home')
          .eq('is_active', true)
          .order('id', { ascending: true })
          .range(from, from + PAGE - 1);
        if (error) throw error;
        (data || []).forEach((r: { account_id: string }) => ids.add(r.account_id));
        if (!data || data.length < PAGE) break;
      }
      setHomeAddressAccountIds(ids);
    } catch (err) {
      console.error('Error loading home address flags:', err);
      setHomeAddressAccountIds(new Set());
    } finally {
      setHomeAddressIdsLoaded(true);
      setLoadingHomeAddressIds(false);
    }
  }, []);

  useEffect(() => {
    loadHomeAddressIds();
  }, [loadHomeAddressIds]);

  const loadApprovalLifecycle = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('crm_account_approvals_with_status')
        .select('account_id, days_to_expiry, approval_is_open')
        .eq('is_current', true);
      if (error) throw error;
      const map = new Map<string, { lifecycle: LifecycleFilter; days: number | null }>();
      (data || []).forEach((row: { account_id: string; days_to_expiry: number | null; approval_is_open: boolean | null }) => {
        map.set(row.account_id, {
          lifecycle: getLifecycle(row.days_to_expiry, !!row.approval_is_open),
          days: row.days_to_expiry ?? null,
        });
      });
      setApprovalLifecycleMap(map);
    } catch (err) {
      console.error('Error loading approval lifecycle map:', err);
      setApprovalLifecycleMap(new Map());
    } finally {
      setApprovalLifecycleLoaded(true);
    }
  }, []);

  const loadPermLifecycle = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('crm_permanent_booking_summary')
        .select('account_id, next_perm_days_remaining')
        .gt('active_perm_booking_count', 0);
      if (error) throw error;
      const map = new Map<string, { lifecycle: LifecycleFilter; days: number | null }>();
      (data || []).forEach((row: { account_id: string; next_perm_days_remaining: number | null }) => {
        map.set(row.account_id, {
          lifecycle: getLifecycle(row.next_perm_days_remaining),
          days: row.next_perm_days_remaining ?? null,
        });
      });
      setPermLifecycleMap(map);
    } catch (err) {
      console.error('Error loading permanent booking lifecycle map:', err);
      setPermLifecycleMap(new Map());
    } finally {
      setPermLifecycleLoaded(true);
    }
  }, []);

  useEffect(() => {
    loadApprovalLifecycle();
    loadPermLifecycle();
  }, [loadApprovalLifecycle, loadPermLifecycle]);

  // These flags (Home Addr., approval/perm lifecycle) are loaded for the whole
  // account set once at mount, so anything edited afterwards -- a home
  // address added in the detail panel, an approval or booking changed -- only
  // showed after a full page reload. Re-read them after edits and on Refresh.
  const refreshAccountFlags = useCallback(() => {
    loadHomeAddressIds();
    loadApprovalLifecycle();
    loadPermLifecycle();
  }, [loadHomeAddressIds, loadApprovalLifecycle, loadPermLifecycle]);

  const loadAccounts = useCallback(async () => {
    setLoading(true);
    try {
      // Shared by both the normal path and the urgency-sort fast path below
      // -- every filter EXCEPT Approval/Perm Bookings, which need different
      // handling depending on whether we're sorting by proximity to expiry.
      const applyBaseFilters = (q) => {
        let query = q;
        if (statusFilter === 'active') query = query.eq('is_active', true);
        if (statusFilter === 'inactive') query = query.eq('is_active', false);

        if (billedFilter === 'billed') query = query.eq('billed_status', 'Billed');
        if (billedFilter === 'non-billed') query = query.eq('billed_status', 'Non-Billed');

        if (seriesFilter.length > 0 && seriesFilter.length < SERIES_KEYS.length) {
          const seriesLabels = seriesFilter.map((k) => SERIES_LABELS[k]).filter(Boolean);
          if (seriesLabels.length > 0) query = query.in('account_series_type', seriesLabels);
        }

        if (categoryFilter === '__none') {
          query = query.or('account_category.is.null,account_category.eq.');
        } else if (categoryFilter !== 'all') {
          query = query.eq('account_category', categoryFilter);
        }

        if (fixedLocationFilter === 'yes') query = query.eq('is_fixed_location_number', true);
        if (fixedLocationFilter === 'no') query = query.eq('is_fixed_location_number', false);

        if (alwaysDivertFilter === 'yes') query = query.eq('always_divert_to_operator', true);
        if (alwaysDivertFilter === 'no') query = query.eq('always_divert_to_operator', false);

        // Home address lives in a separate table, not a crm_accounts column --
        // filtered here via the precomputed id set from loadHomeAddressIds.
        // Skip applying it until that set has loaded once, so we don't
        // transiently filter against an empty set and show zero results.
        if (homeAddressFilter !== 'all' && homeAddressIdsLoaded) {
          const ids = Array.from(homeAddressAccountIds);
          if (homeAddressFilter === 'yes') {
            query = ids.length > 0 ? query.in('account_id', ids) : query.eq('account_id', '__none__');
          } else if (ids.length > 0) {
            query = query.not('account_id', 'in', `(${ids.join(',')})`);
          }
        }

        if (search.trim()) {
          const term = search.trim();
          query = query.or(
            `account_name.ilike.%${term}%,account_id.ilike.%${term}%,contact_name.ilike.%${term}%,email.ilike.%${term}%,phone.ilike.%${term}%`
          );
        }
        return query;
      };

      // Same lifecycle-filter application (Approval/Perm Bookings both live
      // in separate views, not crm_accounts columns) -- shared by both the
      // day-based-sort path below and the normal path, so filtering by
      // Approval/Perm Bookings works no matter which column is sorted.
      const applyLifecycleFilters = (q) => {
        let query = q;
        if (approvalFilter !== 'all' && approvalLifecycleLoaded) {
          if (approvalFilter === 'none') {
            const withApproval = Array.from(approvalLifecycleMap.keys());
            if (withApproval.length > 0) query = query.not('account_id', 'in', `(${withApproval.join(',')})`);
          } else {
            const ids = Array.from(approvalLifecycleMap.entries())
              .filter(([, v]) => v.lifecycle === approvalFilter)
              .map(([id]) => id);
            query = ids.length > 0 ? query.in('account_id', ids) : query.eq('account_id', '__none__');
          }
        }
        if (permBookingFilter !== 'all' && permLifecycleLoaded) {
          if (permBookingFilter === 'none') {
            const withPerm = Array.from(permLifecycleMap.keys());
            if (withPerm.length > 0) query = query.not('account_id', 'in', `(${withPerm.join(',')})`);
          } else {
            const ids = Array.from(permLifecycleMap.entries())
              .filter(([, v]) => v.lifecycle === permBookingFilter)
              .map(([id]) => id);
            query = ids.length > 0 ? query.in('account_id', ids) : query.eq('account_id', '__none__');
          }
        }
        return query;
      };

      // Sorting the "Days Left / Expiry" or "Perm Days / Expiry" columns is
      // meaningless done per-page in account_name order (the whole reason
      // that felt broken) -- days_to_expiry only exists in a separate view,
      // not a crm_accounts column, so it can't be a server .order(). Instead:
      // fetch just the account_id/account_name for every account matching
      // all the OTHER filters (cheap, two columns), rank them client-side
      // using the already-loaded full lifecycle map (accounts with no
      // approval/booking sort to the end, same as the old per-page sort
      // did), then fetch full rows for only the resulting page slice.
      const dayBasedSort =
        sortField === 'approval_days' && approvalLifecycleLoaded
          ? approvalLifecycleMap
          : sortField === 'perm_days' && permLifecycleLoaded
          ? permLifecycleMap
          : null;

      if (dayBasedSort) {
        // Supabase caps an unfiltered/unpaginated select at 1000 rows by
        // default -- with ~4000 accounts and no other filter active, that
        // would silently drop most of the table from the ranking (the
        // exact bug already hit once today on the perm-bookings name
        // lookup). Explicit .limit() well above the real row count avoids it.
        const idQuery = applyLifecycleFilters(applyBaseFilters(supabase.from('crm_accounts').select('account_id, account_name'))).limit(
          20000
        );
        const { data: idRows, error: idErr } = await idQuery;
        if (idErr) throw idErr;

        const sortKey = (id: string): number => {
          const entry = dayBasedSort.get(id);
          if (!entry) return Number.POSITIVE_INFINITY;
          if (entry.lifecycle === 'open') return Number.POSITIVE_INFINITY - 1;
          return entry.days ?? Number.POSITIVE_INFINITY;
        };

        const ordered = ((idRows || []) as { account_id: string; account_name: string }[]).sort((a, b) => {
          const va = sortKey(a.account_id);
          const vb = sortKey(b.account_id);
          if (va !== vb) return va - vb;
          return (a.account_name || '').localeCompare(b.account_name || '');
        });
        const finalOrder = sortAsc ? ordered : ordered.reverse();

        setTotalCount(finalOrder.length);
        const from = page * PAGE_SIZE;
        const pageIds = finalOrder.slice(from, from + PAGE_SIZE).map((r) => r.account_id);

        if (pageIds.length === 0) {
          setAccounts([]);
          return;
        }

        const { data: rows, error: rowErr } = await supabase.from('crm_accounts').select('*').in('account_id', pageIds);
        if (rowErr) throw rowErr;
        const rank = new Map(pageIds.map((id, i) => [id, i]));
        setAccounts(((rows || []) as CRMAccount[]).sort((a, b) => (rank.get(a.account_id) ?? 0) - (rank.get(b.account_id) ?? 0)));
        return;
      }

      let query = applyLifecycleFilters(applyBaseFilters(supabase.from('crm_accounts').select('*', { count: 'exact' })));

      query = query.order(serverSortField, { ascending: sortAsc });

      const from = page * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;
      query = query.range(from, to);

      const { data, error, count } = await query;
      if (error) throw error;

      setAccounts((data || []) as CRMAccount[]);
      setTotalCount(count || 0);
    } catch (err: any) {
      console.error('Error loading CRM accounts:', err);
      toast({
        title: 'Error',
        description: err.message || 'Failed to load CRM accounts',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [
    statusFilter,
    billedFilter,
    seriesFilter,
    categoryFilter,
    fixedLocationFilter,
    alwaysDivertFilter,
    homeAddressFilter,
    homeAddressAccountIds,
    homeAddressIdsLoaded,
    sortField,
    approvalFilter,
    approvalLifecycleMap,
    approvalLifecycleLoaded,
    permBookingFilter,
    permLifecycleMap,
    permLifecycleLoaded,
    search,
    serverSortField,
    sortAsc,
    page,
  ]);

  const loadJobCounts = useCallback(async (accountRows: CRMAccount[]) => {
    if (!accountRows.length) {
      setJobCounts({});
      return;
    }

    setLoadingCounts(true);
    try {
      const accountIds = accountRows.map((a) => a.account_id);

      const { data, error } = await supabase
        .from('taxicaller_data')
        .select('Account_ID')
        .in('Account_ID', accountIds);

      if (error) throw error;

      const map: JobCountMap = {};
      accountIds.forEach((id) => {
        map[id] = 0;
      });

      (data || []).forEach((row: any) => {
        const accountId = row.Account_ID;
        map[accountId] = (map[accountId] || 0) + 1;
      });

      setJobCounts(map);
    } catch (err) {
      console.error('Error loading job counts:', err);
      setJobCounts({});
    } finally {
      setLoadingCounts(false);
    }
  }, []);

  const loadApprovalSummaries = useCallback(async (accountRows: CRMAccount[]) => {
    if (!accountRows.length) {
      setApprovalSummaries({});
      return;
    }

    setLoadingApprovals(true);
    try {
      const accountIds = accountRows.map((a) => a.account_id);

      const { data, error } = await supabase
        .from('crm_account_approvals_with_status')
        .select('account_id, approval_status, days_to_expiry, approval_is_open, approval_to, is_current')
        .in('account_id', accountIds)
        .eq('is_current', true);

      if (error) throw error;

      const map: ApprovalSummaryMap = {};
      (data || []).forEach((row: any) => {
        map[row.account_id] = {
          status: row.approval_status || null,
          daysRemaining: row.days_to_expiry ?? null,
          isOpen: !!row.approval_is_open,
          approvalTo: row.approval_to || null,
        };
      });

      setApprovalSummaries(map);
    } catch (err) {
      console.error('Error loading approval summaries:', err);
      setApprovalSummaries({});
    } finally {
      setLoadingApprovals(false);
    }
  }, []);

  const loadPermBookingSummaries = useCallback(async (accountRows: CRMAccount[]) => {
    if (!accountRows.length) {
      setPermBookingSummaries({});
      return;
    }

    setLoadingPermBookings(true);
    try {
      const accountIds = accountRows.map((a) => a.account_id);

      const { data, error } = await supabase
        .from('crm_permanent_booking_summary')
        .select('account_id, active_perm_booking_count, next_perm_expiry, next_perm_days_remaining')
        .in('account_id', accountIds);

      if (error) throw error;

      const map: PermanentBookingSummaryMap = {};
      (data || []).forEach((row: any) => {
        map[row.account_id] = {
          activeCount: row.active_perm_booking_count || 0,
          nextExpiry: row.next_perm_expiry || null,
          nextDaysRemaining: row.next_perm_days_remaining ?? null,
        };
      });

      setPermBookingSummaries(map);
    } catch (err) {
      console.error('Error loading permanent booking summaries:', err);
      setPermBookingSummaries({});
    } finally {
      setLoadingPermBookings(false);
    }
  }, []);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  useEffect(() => {
    if (accounts.length) {
      loadJobCounts(accounts);
      loadApprovalSummaries(accounts);
      loadPermBookingSummaries(accounts);
    } else {
      setJobCounts({});
      setApprovalSummaries({});
      setPermBookingSummaries({});
    }
  }, [accounts, loadJobCounts, loadApprovalSummaries, loadPermBookingSummaries]);

  const sortedAccounts = useMemo(() => {
    if (!CLIENT_SORT_FIELDS.includes(sortField)) {
      return accounts;
    }

    const lifecycleRank: Record<string, number> = { open: 4, current: 3, expiring: 2, expired: 1, none: 0 };

    const getSortValue = (acc: CRMAccount): number => {
      switch (sortField) {
        case 'job_count':
          return jobCounts[acc.account_id] ?? 0;
        case 'defaults_status': {
          // Inlined rather than calling getDefaultsStatus (defined later in
          // this component) -- that const isn't initialized yet when this
          // useMemo callback runs during the same render pass.
          const total = [acc.default_project, acc.default_cost_code, acc.default_reference].filter((v) =>
            !!v?.trim()
          ).length;
          return total === 3 ? 2 : total > 0 ? 1 : 0;
        }
        case 'approval_status': {
          const summary = approvalSummaries[acc.account_id];
          const lifecycle = summary ? getLifecycle(summary.daysRemaining, summary.isOpen) : 'none';
          return lifecycleRank[lifecycle];
        }
        case 'approval_days': {
          const summary = approvalSummaries[acc.account_id];
          if (!summary) return Number.POSITIVE_INFINITY;
          if (summary.isOpen) return Number.POSITIVE_INFINITY - 1;
          return summary.daysRemaining ?? Number.POSITIVE_INFINITY;
        }
        case 'perm_count':
          return permBookingSummaries[acc.account_id]?.activeCount ?? 0;
        case 'perm_days': {
          const summary = permBookingSummaries[acc.account_id];
          if (!summary || !summary.activeCount) return Number.POSITIVE_INFINITY;
          return summary.nextDaysRemaining ?? Number.POSITIVE_INFINITY;
        }
        case 'home_address':
          return homeAddressAccountIds.has(acc.account_id) ? 1 : 0;
        default:
          return 0;
      }
    };

    const sorted = [...accounts].sort((a, b) => {
      const va = getSortValue(a);
      const vb = getSortValue(b);
      if (va === vb) return 0;
      return va < vb ? -1 : 1;
    });

    return sortAsc ? sorted : sorted.reverse();
  }, [accounts, sortField, sortAsc, approvalSummaries, permBookingSummaries, jobCounts, homeAddressAccountIds]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortAsc((prev) => !prev);
    } else {
      setSortField(field);
      setSortAsc(true);
    }
    setPage(0);
  };

  const renderSortIcon = (field: SortField) => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 opacity-50" />;
    return sortAsc ? <ArrowUp className="w-3 h-3 text-primary" /> : <ArrowDown className="w-3 h-3 text-primary" />;
  };

  const toggleSeries = (s: string) => {
    setSeriesFilter((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));
    setPage(0);
  };

  const handleCreateNew = () => {
    setEditingAccount(null);
    setSubAccountConfig(null);
    setFormOpen(true);
  };

  const handleEdit = (account: CRMAccount, focus?: CRMFormFocus) => {
    setEditingAccount(account);
    setSubAccountConfig(null);
    setEditFocus(focus ?? null);
    setFormOpen(true);
  };

  const handleViewDetail = (account: CRMAccount) => {
    setSelectedAccount(account);
    setDetailOpen(true);
  };

  const handleToggleActive = async (account: CRMAccount) => {
    setTogglingId(account.id);
    try {
      const newActive = !account.is_active;

      const { error } = await supabase
        .from('crm_accounts')
        .update({ is_active: newActive, updated_at: new Date().toISOString() })
        .eq('id', account.id);

      if (error) throw error;

      setAccounts((prev) =>
        prev.map((a) => (a.id === account.id ? { ...a, is_active: newActive } : a))
      );

      toast({
        title: newActive ? 'Account Activated' : 'Account Deactivated',
        description: `${account.account_name} is now ${newActive ? 'active' : 'inactive'}.`,
      });
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.message || 'Failed to update status',
        variant: 'destructive',
      });
    } finally {
      setTogglingId(null);
    }
  };

  const handleCSVExport = () => {
    if (accounts.length === 0) {
      toast({ title: 'No Data', description: 'No accounts to export.' });
      return;
    }

    const headers = [
      'account_id',
      'account_name',
      'account_series_type',
      'billed_status',
      'account_category',
      'hierarchy_type',
      'parent_account_id',
      'contact_name',
      'email',
      'phone',
      'is_active',
    ];

    const csvRows = [headers.join(',')];

    accounts.forEach((a) => {
      const row = headers.map((h) => {
        const val = (a as any)[h];
        if (val === null || val === undefined) return '';
        const str = String(val);
        if (str.includes(',') || str.includes('"') || str.includes('\n')) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      });
      csvRows.push(row.join(','));
    });

    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `crm_accounts_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);

    toast({ title: 'Exported', description: `${accounts.length} accounts exported to CSV.` });
  };

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const getDefaultsStatus = (account: CRMAccount) => {
    const hasProject = !!account.default_project?.trim();
    const hasCostCode = !!account.default_cost_code?.trim();
    const hasReference = !!account.default_reference?.trim();
    const total = [hasProject, hasCostCode, hasReference].filter(Boolean).length;

    return {
      total,
      complete: total === 3,
      partial: total > 0 && total < 3,
      none: total === 0,
    };
  };

  const defaultStatusLabel = (account: CRMAccount) => {
    const defaults = getDefaultsStatus(account);
    if (defaults.complete) return 'Full';
    if (defaults.partial) return 'Partial';
    return 'None';
  };

  return (
    <div className="space-y-4">
      <Card className="bg-card border-border">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <CardTitle className="golden-text text-xl flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                CRM Accounts
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Manage account records, approval status, hierarchy, and linked job visibility.
              </p>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <Button
                variant="outline"
                className="border-border text-muted-foreground"
                onClick={handleCSVExport}
              >
                <Download className="w-4 h-4 mr-1.5" />
                Export CSV
              </Button>

              <Button
                variant="outline"
                className="border-blue-600/60 text-blue-400 hover:bg-blue-900/30"
                onClick={() => setBulkBackfillOpen(true)}
              >
                <Link2 className="w-4 h-4 mr-1.5" />
                Bulk Backfill
              </Button>

              <Button className="golden-button" onClick={handleCreateNew}>
                <Plus className="w-4 h-4 mr-1.5" />
                New Account
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Search + Status + Category */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
            <div className="lg:col-span-2">
              <div className="relative">
                <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  placeholder="Search account, contact, phone, email..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="pl-9"
                />
                {searchInput !== search && (
                  <Loader2 className="w-3.5 h-3.5 text-muted-foreground animate-spin absolute right-3 top-1/2 -translate-y-1/2" />
                )}
              </div>
            </div>


            <Select
              value={statusFilter}
              onValueChange={(v: 'all' | 'active' | 'inactive') => {
                setStatusFilter(v);
                setPage(0);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={categoryFilter}
              onValueChange={(v) => {
                setCategoryFilter(v);
                setPage(0);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                {CATEGORY_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Pill-style filters (Billed / Series) — matches Export Builder */}
          <div className="flex flex-wrap items-center gap-4 rounded-lg border border-border bg-secondary/20 px-3 py-2.5">
            {/* Billed pills */}
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-muted-foreground uppercase tracking-wide">Billing</span>
              <div className="flex gap-1">
                {[
                  { key: 'all', label: 'All' },
                  { key: 'billed', label: 'Billed' },
                  { key: 'non-billed', label: 'Non-Billed' },
                ].map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => {
                      setBilledFilter(opt.key as any);
                      setPage(0);
                    }}
                    className={`px-2.5 py-1 rounded text-xs font-medium border transition-colors ${
                      billedFilter === opt.key
                        ? 'bg-primary/20 text-primary border-primary/50'
                        : 'bg-secondary/40 text-muted-foreground border-border hover:border-primary/30'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="h-5 w-px bg-border" />

            {/* Series pills */}
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-muted-foreground uppercase tracking-wide">Series</span>
              <div className="flex gap-1">
                {SERIES_KEYS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => toggleSeries(s)}
                    title={`${SERIES_LABELS[s]} (${s}xxx)`}
                    className={`px-2.5 py-1 rounded text-xs font-medium border transition-colors ${
                      seriesFilter.includes(s)
                        ? 'bg-primary/20 text-primary border-primary/50'
                        : 'bg-secondary/40 text-muted-foreground border-border hover:border-primary/30'
                    }`}
                  >
                    {s}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => {
                    setSeriesFilter(SERIES_KEYS);
                    setPage(0);
                  }}
                  className="px-2 py-1 rounded text-[10px] font-medium border border-border/40 text-muted-foreground hover:border-primary/40 hover:text-primary transition-colors"
                >
                  All
                </button>
              </div>
            </div>

            <div className="h-5 w-px bg-border" />

            {/* Fixed Location pills */}
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-muted-foreground uppercase tracking-wide">Fixed Location</span>
              <div className="flex gap-1">
                {(
                  [
                    { key: 'all', label: 'All' },
                    { key: 'yes', label: 'Yes' },
                    { key: 'no', label: 'No' },
                  ] as { key: TriFilter; label: string }[]
                ).map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => {
                      setFixedLocationFilter(opt.key);
                      setPage(0);
                    }}
                    className={`px-2.5 py-1 rounded text-xs font-medium border transition-colors ${
                      fixedLocationFilter === opt.key
                        ? 'bg-primary/20 text-primary border-primary/50'
                        : 'bg-secondary/40 text-muted-foreground border-border hover:border-primary/30'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Always Divert pills */}
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-muted-foreground uppercase tracking-wide">Always Divert</span>
              <div className="flex gap-1">
                {(
                  [
                    { key: 'all', label: 'All' },
                    { key: 'yes', label: 'Yes' },
                    { key: 'no', label: 'No' },
                  ] as { key: TriFilter; label: string }[]
                ).map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => {
                      setAlwaysDivertFilter(opt.key);
                      setPage(0);
                    }}
                    className={`px-2.5 py-1 rounded text-xs font-medium border transition-colors ${
                      alwaysDivertFilter === opt.key
                        ? 'bg-primary/20 text-primary border-primary/50'
                        : 'bg-secondary/40 text-muted-foreground border-border hover:border-primary/30'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Home Address pills */}
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-muted-foreground uppercase tracking-wide">Home Address</span>
              <div className="flex gap-1">
                {(
                  [
                    { key: 'all', label: 'All' },
                    { key: 'yes', label: 'Yes' },
                    { key: 'no', label: 'No' },
                  ] as { key: TriFilter; label: string }[]
                ).map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => {
                      setHomeAddressFilter(opt.key);
                      setPage(0);
                    }}
                    className={`px-2.5 py-1 rounded text-xs font-medium border transition-colors ${
                      homeAddressFilter === opt.key
                        ? 'bg-primary/20 text-primary border-primary/50'
                        : 'bg-secondary/40 text-muted-foreground border-border hover:border-primary/30'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="h-5 w-px bg-border" />

            {/* Approval pills -- filters the whole account set (not just the
                current page) by the same lifecycle the Approval column shows. */}
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-muted-foreground uppercase tracking-wide">Approval</span>
              <div className="flex gap-1">
                {(
                  [
                    { key: 'all', label: 'All' },
                    { key: 'expired', label: 'Expired' },
                    { key: 'expiring', label: 'Expiring' },
                    { key: 'current', label: 'Current' },
                    { key: 'open', label: 'Open' },
                    { key: 'none', label: 'None' },
                  ] as { key: LifecycleFilter; label: string }[]
                ).map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => {
                      setApprovalFilter(opt.key);
                      if (opt.key === 'expired' || opt.key === 'expiring' || opt.key === 'current') {
                        setSortField('approval_days');
                        setSortAsc(true);
                      }
                      setPage(0);
                    }}
                    className={`px-2.5 py-1 rounded text-xs font-medium border transition-colors ${
                      approvalFilter === opt.key
                        ? 'bg-primary/20 text-primary border-primary/50'
                        : 'bg-secondary/40 text-muted-foreground border-border hover:border-primary/30'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Perm Bookings pills -- same idea, from the permanent booking
                summary view. No "Open" bucket -- perm bookings always have
                a real expiry or none at all, never an open-ended approval. */}
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-muted-foreground uppercase tracking-wide">Perm Bookings</span>
              <div className="flex gap-1">
                {(
                  [
                    { key: 'all', label: 'All' },
                    { key: 'expired', label: 'Expired' },
                    { key: 'expiring', label: 'Expiring' },
                    { key: 'current', label: 'Current' },
                    { key: 'none', label: 'None' },
                  ] as { key: LifecycleFilter; label: string }[]
                ).map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => {
                      setPermBookingFilter(opt.key);
                      if (opt.key === 'expired' || opt.key === 'expiring' || opt.key === 'current') {
                        setSortField('perm_days');
                        setSortAsc(true);
                      }
                      setPage(0);
                    }}
                    className={`px-2.5 py-1 rounded text-xs font-medium border transition-colors ${
                      permBookingFilter === opt.key
                        ? 'bg-primary/20 text-primary border-primary/50'
                        : 'bg-secondary/40 text-muted-foreground border-border hover:border-primary/30'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex-1" />

            {/* Legend */}
            <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
              <span className="flex items-center gap-1">
                <span className="inline-block w-2 h-2 rounded-full bg-green-500" /> Current (&gt;90d)
              </span>
              <span className="flex items-center gap-1">
                <span className="inline-block w-2 h-2 rounded-full bg-amber-500" /> Expiring (≤90d)
              </span>
              <span className="flex items-center gap-1">
                <span className="inline-block w-2 h-2 rounded-full bg-red-500" /> Expired
              </span>
            </div>
          </div>

          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
              <span className="flex items-center gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5" />
                Approval status is read from approvals
              </span>
              <span className="flex items-center gap-1.5">
                <CalendarClock className="w-3.5 h-3.5" />
                Perm booking status is read from permanent booking summary
              </span>
            </div>

            <Button
              variant="ghost"
              className="text-muted-foreground"
              onClick={() => {
                loadAccounts();
                refreshAccountFlags();
              }}
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Loading CRM accounts...
            </div>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-border">
              <Table>
                <TableHeader>
                  <TableRow className="border-border">
                    <TableHead>
                      <button
                        type="button"
                        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                        onClick={() => handleSort('account_id')}
                      >
                        Account ID
                        {renderSortIcon('account_id')}
                      </button>
                    </TableHead>

                    <TableHead>
                      <button
                        type="button"
                        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                        onClick={() => handleSort('account_name')}
                      >
                        Account Name
                        {renderSortIcon('account_name')}
                      </button>
                    </TableHead>

                    <TableHead>
                      <button
                        type="button"
                        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                        onClick={() => handleSort('account_category')}
                      >
                        Category
                        {renderSortIcon('account_category')}
                      </button>
                    </TableHead>
                    <TableHead className="text-center">
                      <button
                        type="button"
                        className="flex items-center gap-1 mx-auto text-xs text-muted-foreground hover:text-foreground"
                        onClick={() => handleSort('is_fixed_location_number')}
                      >
                        Fixed Loc.
                        {renderSortIcon('is_fixed_location_number')}
                      </button>
                    </TableHead>
                    <TableHead className="text-center">
                      <button
                        type="button"
                        className="flex items-center gap-1 mx-auto text-xs text-muted-foreground hover:text-foreground"
                        onClick={() => handleSort('always_divert_to_operator')}
                      >
                        Always Divert
                        {renderSortIcon('always_divert_to_operator')}
                      </button>
                    </TableHead>
                    <TableHead className="text-center">
                      <button
                        type="button"
                        className="flex items-center gap-1 mx-auto text-xs text-muted-foreground hover:text-foreground"
                        onClick={() => handleSort('home_address')}
                      >
                        Home Addr.
                        {renderSortIcon('home_address')}
                      </button>
                    </TableHead>
                    <TableHead className="text-center">
                      <button
                        type="button"
                        className="flex items-center gap-1 mx-auto text-xs text-muted-foreground hover:text-foreground"
                        onClick={() => handleSort('job_count')}
                      >
                        Jobs
                        {renderSortIcon('job_count')}
                      </button>
                    </TableHead>
                    <TableHead className="text-center">
                      <button
                        type="button"
                        className="flex items-center gap-1 mx-auto text-xs text-muted-foreground hover:text-foreground"
                        onClick={() => handleSort('defaults_status')}
                      >
                        Defaults
                        {renderSortIcon('defaults_status')}
                      </button>
                    </TableHead>
                    <TableHead>
                      <button
                        type="button"
                        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                        onClick={() => handleSort('approval_status')}
                      >
                        Approval
                        {renderSortIcon('approval_status')}
                      </button>
                    </TableHead>
                    <TableHead>
                      <button
                        type="button"
                        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                        onClick={() => handleSort('approval_days')}
                      >
                        Days Left / Expiry
                        {renderSortIcon('approval_days')}
                      </button>
                    </TableHead>
                    <TableHead className="text-center">
                      <button
                        type="button"
                        className="flex items-center gap-1 mx-auto text-xs text-muted-foreground hover:text-foreground"
                        onClick={() => handleSort('perm_count')}
                      >
                        Perm Books
                        {renderSortIcon('perm_count')}
                      </button>
                    </TableHead>
                    <TableHead>
                      <button
                        type="button"
                        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                        onClick={() => handleSort('perm_days')}
                      >
                        Perm Days / Expiry
                        {renderSortIcon('perm_days')}
                      </button>
                    </TableHead>
                    <TableHead className="text-center">
                      <button
                        type="button"
                        className="flex items-center gap-1 mx-auto text-xs text-muted-foreground hover:text-foreground"
                        onClick={() => handleSort('is_active')}
                      >
                        Active
                        {renderSortIcon('is_active')}
                      </button>
                    </TableHead>
                    <TableHead className="text-xs text-muted-foreground text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>

                <TableBody>
                  {sortedAccounts.map((account) => {
                    const defaults = getDefaultsStatus(account);
                    const jCount = jobCounts[account.account_id] ?? 0;
                    const approval = approvalSummaries[account.account_id];
                    const approvalLifecycle = approval
                      ? getLifecycle(approval.daysRemaining, approval.isOpen)
                      : 'none';

                    const permSummary = permBookingSummaries[account.account_id];
                    const permHasActive = !!(permSummary?.activeCount && permSummary.activeCount > 0);
                    const permLifecycle = permHasActive
                      ? getLifecycle(permSummary?.nextDaysRemaining ?? null)
                      : 'none';

                    return (
                      <TableRow key={account.id} className="border-border">
                        <TableCell className="font-mono text-xs whitespace-nowrap">
                          {account.account_id}
                        </TableCell>

                        <TableCell>
                          <div className="min-w-[220px]">
                            <p className="text-sm font-medium text-foreground">{account.account_name}</p>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {account.account_series_type && (
                                <Badge className="bg-blue-900/30 text-blue-400 border-blue-700/50 text-[10px]">
                                  {account.account_series_type}
                                </Badge>
                              )}
                              {account.billed_status && (
                                <Badge
                                  className={`text-[10px] ${
                                    account.billed_status === 'Billed'
                                      ? 'bg-green-900/30 text-green-400 border-green-700/50'
                                      : 'bg-secondary text-muted-foreground border-border'
                                  }`}
                                >
                                  {account.billed_status}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </TableCell>

                        <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                          {account.account_category || '-'}
                        </TableCell>

                        <TableCell className="text-center">
                          {account.is_fixed_location_number ? (
                            <Badge className="bg-green-900/30 text-green-400 border-green-700/50 text-[10px]">
                              Yes
                            </Badge>
                          ) : (
                            <span className="text-xs text-muted-foreground">-</span>
                          )}
                        </TableCell>

                        <TableCell className="text-center">
                          {account.always_divert_to_operator ? (
                            <Badge className="bg-amber-900/30 text-amber-400 border-amber-700/50 text-[10px]">
                              Yes
                            </Badge>
                          ) : (
                            <span className="text-xs text-muted-foreground">-</span>
                          )}
                        </TableCell>

                        <TableCell className="text-center">
                          {loadingHomeAddressIds ? (
                            <Loader2 className="w-3 h-3 animate-spin mx-auto text-muted-foreground" />
                          ) : homeAddressAccountIds.has(account.account_id) ? (
                            <Badge className="bg-blue-900/30 text-blue-400 border-blue-700/50 text-[10px]">
                              Yes
                            </Badge>
                          ) : (
                            <span className="text-xs text-muted-foreground">-</span>
                          )}
                        </TableCell>

                        <TableCell className="text-center">
                          {loadingCounts ? (
                            <Loader2 className="w-3 h-3 animate-spin mx-auto text-muted-foreground" />
                          ) : jCount > 0 ? (
                            <Badge
                              variant="secondary"
                              className="text-xs bg-blue-900/30 text-blue-400 border-blue-700/50"
                            >
                              {jCount}
                            </Badge>
                          ) : (
                            <span className="text-xs text-muted-foreground">0</span>
                          )}
                        </TableCell>

                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            {defaults.complete ? (
                              <CheckCircle2 className="w-4 h-4 text-green-400" />
                            ) : defaults.partial ? (
                              <AlertTriangle className="w-4 h-4 text-yellow-400" />
                            ) : (
                              <XCircle className="w-4 h-4 text-muted-foreground/40" />
                            )}
                            <span className="text-[11px] text-muted-foreground">{defaultStatusLabel(account)}</span>
                          </div>
                        </TableCell>

                        <TableCell>
                          {loadingApprovals ? (
                            <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />
                          ) : approval ? (
                            <Badge className={getLifecycleBadgeClass(approvalLifecycle)}>
                              {getLifecycleLabel(approvalLifecycle)}
                            </Badge>
                          ) : (
                            <Badge className="bg-secondary text-muted-foreground border-border">
                              None
                            </Badge>
                          )}
                        </TableCell>

                        <TableCell className="whitespace-nowrap">
                          {loadingApprovals ? (
                            <span className="text-muted-foreground text-xs">...</span>
                          ) : approval ? (
                            <div className="flex flex-col leading-tight">
                              <span className={`text-xs ${getDaysCellClass(approvalLifecycle)}`}>
                                {formatDaysText(approval.daysRemaining, approval.isOpen)}
                              </span>
                              <span className="text-[10px] text-muted-foreground">
                                {approval.isOpen ? 'No end date' : formatDate(approval.approvalTo)}
                              </span>
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground">-</span>
                          )}
                        </TableCell>

                        <TableCell className="text-center">
                          {loadingPermBookings ? (
                            <Loader2 className="w-3 h-3 animate-spin mx-auto text-muted-foreground" />
                          ) : permHasActive ? (
                            <Badge className="bg-purple-900/30 text-purple-400 border-purple-700/50 text-xs">
                              {permSummary!.activeCount}
                            </Badge>
                          ) : (
                            <span className="text-xs text-muted-foreground">No</span>
                          )}
                        </TableCell>

                        <TableCell className="whitespace-nowrap">
                          {loadingPermBookings ? (
                            <span className="text-muted-foreground text-xs">...</span>
                          ) : permHasActive ? (
                            <div className="flex flex-col leading-tight">
                              <span className={`text-xs ${getDaysCellClass(permLifecycle)}`}>
                                {formatDaysText(permSummary?.nextDaysRemaining ?? null)}
                              </span>
                              <span className="text-[10px] text-muted-foreground">
                                {formatDate(permSummary?.nextExpiry)}
                              </span>
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground">-</span>
                          )}
                        </TableCell>

                        <TableCell className="text-center">
                          <Switch
                            checked={!!account.is_active}
                            onCheckedChange={() => handleToggleActive(account)}
                            disabled={togglingId === account.id}
                          />
                        </TableCell>

                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-border text-muted-foreground"
                            onClick={() => handleViewDetail(account)}
                          >
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}

                  {sortedAccounts.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={14} className="text-center py-10 text-muted-foreground">
                        No CRM accounts found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex-shrink-0 flex items-center justify-between px-1 py-1">
              <span className="text-xs text-muted-foreground">
                Page {page + 1} of {totalPages} ({totalCount} total)
                {CLIENT_SORT_FIELDS.includes(sortField) && (
                  <span className="ml-2 text-amber-400/80">
                    · Sorting within current page
                  </span>
                )}
              </span>
              <div className="flex gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={page === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  className="text-muted-foreground"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Prev
                </Button>

                <Button
                  size="sm"
                  variant="ghost"
                  disabled={page >= totalPages - 1}
                  onClick={() => setPage((p) => p + 1)}
                  className="text-muted-foreground"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <CRMAccountForm
        open={formOpen}
        onOpenChange={(open) => {
          setFormOpen(open);
          if (!open) {
            setSubAccountConfig(null);
            setEditFocus(null);
            refreshAccountFlags();

            // Came from a client's detail panel -> go back to it, freshly
            // loaded so the overview tiles show whatever was just saved.
            const returnTo = reopenDetailAfterEdit.current;
            reopenDetailAfterEdit.current = null;
            if (returnTo) {
              supabase
                .from('crm_accounts')
                .select('*')
                .eq('account_id', returnTo)
                .maybeSingle()
                .then(({ data }) => {
                  if (data) {
                    setSelectedAccount(data as CRMAccount);
                    setDetailOpen(true);
                  }
                });
            }
          }
        }}
        focus={editFocus}
        account={editingAccount}
        onSaved={() => {
          loadAccounts();
          refreshAccountFlags();
        }}
        subAccountConfig={subAccountConfig}
      />

      <CRMAccountDetail
        open={detailOpen}
        onOpenChange={(open) => {
          setDetailOpen(open);
          if (!open) refreshAccountFlags();
        }}
        account={selectedAccount}
        onEdit={(acc, focus) => {
          setDetailOpen(false);
          reopenDetailAfterEdit.current = acc.account_id;
          handleEdit(acc, focus);
        }}
        onCreateSubAccount={(parentId, parentName, mode) => {
          setDetailOpen(false);
          setEditingAccount(null);
          setSubAccountConfig({ parentAccountId: parentId, parentAccountName: parentName, mode });
          setFormOpen(true);
        }}
        onDeleted={() => loadAccounts()}
        onActiveChanged={(accountId, isActive) => {
          // Keep the list row in step; the open panel already shows the new
          // state itself (updating selectedAccount here would reload it).
          setAccounts((prev) => prev.map((a) => (a.account_id === accountId ? { ...a, is_active: isActive } : a)));
        }}
      />

      <BulkBackfillDialog
        open={bulkBackfillOpen}
        onOpenChange={setBulkBackfillOpen}
        onComplete={() => loadAccounts()}
      />
    </div>
  );
};

export default CRMAccountsPage;
